import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useSystemSettings } from '@/hooks/useSystemSettings';
import { useAuth } from '@/contexts/AuthContext';
import { useUserSession } from '@/hooks/useUserSession';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { 
  HelpCircle, 
  MessageCircle, 
  Mail, 
  Phone, 
  Book,
  Send,
  CheckCircle,
  AlertCircle
} from 'lucide-react';

const Support = () => {
  const { toast } = useToast();
  const { data: settings } = useSystemSettings();
  const { user } = useAuth();
  const { sessionData } = useUserSession();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    category: '',
    subject: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [ticketNumber, setTicketNumber] = useState('');
  const [formErrors, setFormErrors] = useState<{[key: string]: string}>({});

  // Pre-fill form data if user is logged in or has session data
  React.useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        name: user.user_metadata?.full_name || '',
        email: user.email || ''
      }));
    } else if (sessionData) {
      setFormData(prev => ({
        ...prev,
        name: sessionData.fullName || '',
        email: sessionData.email || ''
      }));
    }
  }, [user, sessionData]);

  const validateForm = () => {
    const errors: {[key: string]: string} = {};
    
    if (!formData.name.trim()) errors.name = 'Name is required';
    if (!formData.email.trim()) errors.email = 'Email is required';
    if (!formData.email.includes('@')) errors.email = 'Please enter a valid email';
    if (!formData.category) errors.category = 'Please select a category';
    if (!formData.subject.trim()) errors.subject = 'Subject is required';
    if (!formData.message.trim()) errors.message = 'Message is required';
    if (formData.message.trim().length < 10) errors.message = 'Message must be at least 10 characters';
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast({
        title: "Please fix the errors",
        description: "Please correct the highlighted fields and try again.",
        variant: "destructive"
      });
      return;
    }
    
    setSubmitting(true);

    try {
      // Call the edge function to send support email
      const { data, error } = await supabase.functions.invoke('send-support-email', {
        body: {
          name: formData.name.trim(),
          email: formData.email.trim(),
          category: formData.category,
          subject: formData.subject.trim(),
          message: formData.message.trim(),
          userId: user?.id || null,
        },
      });

      if (error) throw error;

      console.log('Support ticket response:', data);
      
      toast({
        title: "Support Ticket Created!",
        description: `Your ticket #${data.ticketNumber} has been created. We'll respond within ${data.priority === 'high' ? '12' : '24'} hours.`,
      });
      
      setTicketNumber(data.ticketNumber);
      setSubmitted(true);
      
      // Reset form but keep user info
      setFormData({
        name: formData.name,
        email: formData.email,
        category: '',
        subject: '',
        message: ''
      });
      setFormErrors({});
    } catch (error: any) {
      console.error('Support form error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to send message. Please try again.",
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  const getFieldError = (fieldName: string) => {
    return formErrors[fieldName] ? (
      <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
        <AlertCircle className="h-3 w-3" />
        {formErrors[fieldName]}
      </p>
    ) : null;
  };

  const faqs = [
    {
      question: "How do I upgrade my subscription?",
      answer: "You can upgrade your subscription anytime from your dashboard. Go to the billing section and select your new plan. The change will take effect immediately."
    },
    {
      question: "What happens if I exceed my plan limits?",
      answer: "We'll notify you when you're approaching your limits. You can upgrade to a higher plan or we'll temporarily restrict certain features until the next billing cycle."
    },
    {
      question: "How do I cancel my subscription?",
      answer: "You can cancel your subscription from the billing management portal. Your access will continue until the end of your current billing period."
    },
    {
      question: "Do you offer refunds?",
      answer: "We offer a 30-day money-back guarantee for all new subscriptions. Contact support for refund requests."
    },
    {
      question: "Can I get a custom plan for my organization?",
      answer: "Yes! We offer custom Enterprise plans for larger organizations. Contact our sales team to discuss your specific needs."
    },
    {
      question: "How secure is my data?",
      answer: "We use industry-standard encryption and security measures. All data is encrypted in transit and at rest, and we're SOC 2 Type II compliant."
    }
  ];

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-2xl mx-auto text-center">
            <CheckCircle className="h-16 w-16 text-green-600 mx-auto mb-6" />
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              Support Ticket Created
            </h1>
            <p className="text-xl text-gray-600 mb-6">
              Thank you for contacting us! Your ticket #{ticketNumber} has been created.
            </p>
            <div className="bg-white rounded-lg p-6 mb-8 border">
              <h3 className="font-semibold text-gray-900 mb-2">What happens next?</h3>
              <ul className="text-left text-gray-600 space-y-2">
                <li>• We'll review your request and respond within 24 hours</li>
                <li>• You'll receive email updates on your ticket status</li>
                <li>• Keep your ticket number #{ticketNumber} for reference</li>
              </ul>
            </div>
            <Button onClick={() => setSubmitted(false)} className="mr-4">
              Submit Another Request
            </Button>
            <Button variant="outline" onClick={() => window.location.href = '/'}>
              Back to Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
      <div className="container mx-auto px-4 py-16">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            How can we help you?
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Get the support you need with our comprehensive help center and dedicated support team.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {/* Contact Form */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center text-xl font-semibold">
                <MessageCircle className="h-5 w-5 mr-2 text-blue-600" />
                Contact Support
              </CardTitle>
              <CardDescription>
                Send us a message and we'll respond within 24 hours
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className={formErrors.name ? 'border-red-500' : ''}
                    />
                    {getFieldError('name')}
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className={formErrors.email ? 'border-red-500' : ''}
                    />
                    {getFieldError('email')}
                  </div>
                </div>

                <div>
                  <Label htmlFor="category">Category *</Label>
                  <Select 
                    value={formData.category} 
                    onValueChange={(value) => setFormData({...formData, category: value})}
                  >
                    <SelectTrigger className={formErrors.category ? 'border-red-500' : ''}>
                      <SelectValue placeholder="Select a category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="billing">Billing & Payments</SelectItem>
                      <SelectItem value="technical">Technical Support</SelectItem>
                      <SelectItem value="account">Account Management</SelectItem>
                      <SelectItem value="feature">Feature Request</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  {getFieldError('category')}
                </div>

                <div>
                  <Label htmlFor="subject">Subject *</Label>
                  <Input
                    id="subject"
                    value={formData.subject}
                    onChange={(e) => setFormData({...formData, subject: e.target.value})}
                    className={formErrors.subject ? 'border-red-500' : ''}
                  />
                  {getFieldError('subject')}
                </div>

                <div>
                  <Label htmlFor="message">Message *</Label>
                  <Textarea
                    id="message"
                    rows={5}
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    className={formErrors.message ? 'border-red-500' : ''}
                    placeholder="Please describe your issue or question in detail..."
                  />
                  {getFieldError('message')}
                </div>

                <Button type="submit" disabled={submitting} className="w-full">
                  {submitting ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Sending...
                    </div>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Send Message
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Contact Options */}
          <div className="space-y-6">
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center text-xl font-semibold">
                  <HelpCircle className="h-5 w-5 mr-2 text-blue-600" />
                  Other Ways to Reach Us
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start space-x-3 p-3 rounded-lg bg-blue-50">
                  <Mail className="h-5 w-5 text-blue-600 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Email Support</h3>
                    <p className="text-gray-600 text-sm">{settings?.support_email || 'support@example.com'}</p>
                    <p className="text-gray-500 text-xs">Response within 24 hours</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3 p-3 rounded-lg bg-green-50">
                  <Phone className="h-5 w-5 text-green-600 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Phone Support</h3>
                    <p className="text-gray-600 text-sm">+1 (555) 123-4567</p>
                    <p className="text-gray-500 text-xs">Mon-Fri, 9 AM - 6 PM EST</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3 p-3 rounded-lg bg-purple-50">
                  <Book className="h-5 w-5 text-purple-600 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Documentation</h3>
                    <p className="text-gray-600 text-sm">Comprehensive guides and tutorials</p>
                    <Button variant="link" className="text-purple-600 p-0 h-auto text-xs">
                      Browse Documentation →
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Live Chat Widget Placeholder */}
            <Card className="border-0 shadow-lg">
              <CardContent className="p-6">
                <div className="text-center">
                  <MessageCircle className="h-8 w-8 text-blue-600 mx-auto mb-3" />
                  <h3 className="font-semibold text-gray-900 mb-2">Need Immediate Help?</h3>
                  <p className="text-gray-600 text-sm mb-4">
                    Chat with our support team for instant assistance
                  </p>
                  <Button className="w-full">
                    Start Live Chat
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mt-16">
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-8">
            Frequently Asked Questions
          </h2>
          <Card className="border-0 shadow-lg max-w-4xl mx-auto">
            <CardContent className="p-6">
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq, index) => (
                  <AccordionItem key={index} value={`item-${index}`}>
                    <AccordionTrigger className="text-left">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-gray-600">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Support;
